/**
 * In-memory + sessionStorage cache for client portal project-scoped API data.
 * Deduplicates in-flight requests and avoids refetch on every page navigation.
 */

const SESSION_PREFIX = 'reimagine_client_data_v1_';

export type ClientDataCacheTag =
  | 'capabilities'
  | 'selectedCapabilities'
  | 'projectState'
  | 'assessmentLegacy'
  | 'dashboardAssessment'
  | 'dynamicAssessmentResponse'
  | 'chat'
  | 'reports'
  | 'portalSnapshot';

type CacheEntry<T = unknown> = {
  data: T;
  timestamp: number;
  tags: ClientDataCacheTag[];
  projectId?: string;
  userId?: string;
};

const memory = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<unknown>>();

export const CLIENT_DATA_DEFAULT_TTL_MS = 5 * 60 * 1000;
export const CLIENT_DATA_SHORT_TTL_MS = 60 * 1000;
export const CLIENT_DATA_CAPABILITIES_TTL_MS = 15 * 60 * 1000;

function sessionKey(key: string) {
  return `${SESSION_PREFIX}${key}`;
}

function readSession<T>(key: string): CacheEntry<T> | null {
  try {
    const raw = sessionStorage.getItem(sessionKey(key));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEntry<T>;
    if (!parsed || typeof parsed.timestamp !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeSession<T>(key: string, entry: CacheEntry<T>) {
  try {
    sessionStorage.setItem(sessionKey(key), JSON.stringify(entry));
  } catch {
    // quota / private mode
  }
}

function isFresh(entry: CacheEntry | null | undefined, ttlMs: number) {
  if (!entry) return false;
  return Date.now() - entry.timestamp <= ttlMs;
}

export function peekClientDataCache<T>(
  key: string,
  ttlMs: number = CLIENT_DATA_DEFAULT_TTL_MS
): T | null {
  const mem = memory.get(key) as CacheEntry<T> | undefined;
  if (isFresh(mem, ttlMs)) {
    return mem!.data;
  }
  const stored = readSession<T>(key);
  if (isFresh(stored, ttlMs)) {
    memory.set(key, stored!);
    return stored!.data;
  }
  return null;
}

export type CachedFetchOptions = {
  ttlMs?: number;
  tags?: ClientDataCacheTag[];
  projectId?: string | number | null;
  userId?: string | number | null;
  /** When true, return stale cache immediately and refresh in the background. */
  revalidate?: boolean;
};

export async function cachedClientFetch<T>(
  key: string,
  fetcher: () => Promise<T>,
  options: CachedFetchOptions = {}
): Promise<T> {
  const ttlMs = options.ttlMs ?? CLIENT_DATA_DEFAULT_TTL_MS;
  const cached = peekClientDataCache<T>(key, ttlMs);
  if (cached !== null) {
    return cached;
  }

  if (inflight.has(key)) {
    return inflight.get(key) as Promise<T>;
  }

  const promise = fetcher()
    .then((data) => {
      const entry: CacheEntry<T> = {
        data,
        timestamp: Date.now(),
        tags: options.tags ?? [],
        projectId:
          options.projectId != null && options.projectId !== ''
            ? String(options.projectId)
            : undefined,
        userId:
          options.userId != null && options.userId !== ''
            ? String(options.userId)
            : undefined,
      };
      memory.set(key, entry);
      writeSession(key, entry);
      inflight.delete(key);
      return data;
    })
    .catch((err) => {
      inflight.delete(key);
      throw err;
    });

  inflight.set(key, promise);
  return promise;
}

/**
 * Stale-while-revalidate: returns cached data when present (even if TTL expired),
 * and optionally kicks off a background refresh when stale or missing.
 */
export async function cachedClientFetchSWR<T>(
  key: string,
  fetcher: () => Promise<T>,
  options: CachedFetchOptions = {}
): Promise<{ data: T | null; fromCache: boolean; refresh: Promise<T> | null }> {
  const ttlMs = options.ttlMs ?? CLIENT_DATA_DEFAULT_TTL_MS;
  const mem = memory.get(key) as CacheEntry<T> | undefined;
  const stored = mem ?? readSession<T>(key);
  const fresh = isFresh(stored ?? undefined, ttlMs);
  const hasAny = stored != null;

  if (fresh && stored) {
    memory.set(key, stored);
    return { data: stored.data, fromCache: true, refresh: null };
  }

  if (hasAny && stored && options.revalidate !== false) {
    const refresh = cachedClientFetch(key, fetcher, options).catch(() => stored.data);
    return { data: stored.data, fromCache: true, refresh };
  }

  const data = await cachedClientFetch(key, fetcher, options);
  return { data, fromCache: false, refresh: null };
}

export function invalidateClientDataCache(filter?: {
  projectId?: string | number | null;
  userId?: string | number | null;
  tags?: ClientDataCacheTag[];
}) {
  const projectId =
    filter?.projectId != null && filter.projectId !== ''
      ? String(filter.projectId)
      : null;
  const userId =
    filter?.userId != null && filter.userId !== '' ? String(filter.userId) : null;
  const tags = filter?.tags?.length ? new Set(filter.tags) : null;

  const shouldDrop = (entry: CacheEntry, key: string) => {
    if (projectId && entry.projectId && entry.projectId !== projectId) return false;
    if (userId && entry.userId && entry.userId !== userId) return false;
    if (tags && !entry.tags.some((t) => tags.has(t))) return false;
    if (!tags && !projectId && !userId) return true;
    if (projectId || userId || tags) return true;
    return false;
  };

  for (const key of [...memory.keys()]) {
    const entry = memory.get(key);
    if (entry && shouldDrop(entry, key)) {
      memory.delete(key);
      inflight.delete(key);
      sessionStorage.removeItem(sessionKey(key));
    }
  }

  if (!filter) {
    inflight.clear();
    return;
  }

  for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
    const storageKey = sessionStorage.key(i);
    if (!storageKey?.startsWith(SESSION_PREFIX)) continue;
    const logicalKey = storageKey.slice(SESSION_PREFIX.length);
    if (memory.has(logicalKey)) continue;
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) continue;
      const entry = JSON.parse(raw) as CacheEntry;
      if (entry && shouldDrop(entry, logicalKey)) {
        sessionStorage.removeItem(storageKey);
      }
    } catch {
      sessionStorage.removeItem(storageKey);
    }
  }
}

export function clearAllClientDataCache() {
  memory.clear();
  inflight.clear();
  for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
    const key = sessionStorage.key(i);
    if (key?.startsWith(SESSION_PREFIX)) {
      sessionStorage.removeItem(key);
    }
  }
}

export function clientCacheKey(parts: Array<string | number | null | undefined>) {
  return parts.filter((p) => p != null && p !== '').join('_');
}

/** Limit concurrent async work (e.g. dynamic assessment response loads). */
export function invalidateAfterCapabilitySelectionChange(
  userId: string | number,
  projectId: string | number
) {
  invalidateClientDataCache({
    userId,
    projectId,
    tags: [
      'selectedCapabilities',
      'projectState',
      'portalSnapshot',
      'dashboardAssessment',
    ],
  });
}

export function invalidateAfterAssessmentChange(
  userId: string | number,
  projectId: string | number
) {
  invalidateClientDataCache({
    userId,
    projectId,
    tags: [
      'assessmentLegacy',
      'dashboardAssessment',
      'projectState',
      'portalSnapshot',
      'dynamicAssessmentResponse',
    ],
  });
}

export function invalidateAfterChatChange(userId: string | number, projectId: string | number) {
  invalidateClientDataCache({
    userId,
    projectId,
    tags: ['chat', 'portalSnapshot'],
  });
}

export function invalidateAfterReportChange(projectId: string | number) {
  invalidateClientDataCache({
    projectId,
    tags: ['reports', 'portalSnapshot'],
  });
}

export async function mapWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  mapper: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  if (items.length === 0) return [];
  const limit = Math.max(1, concurrency);
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await mapper(items[index], index);
    }
  }

  const workers = Array.from({ length: Math.min(limit, items.length) }, () => worker());
  await Promise.all(workers);
  return results;
}
